import { useState, useEffect } from 'react';
import { scorekeeperService, ScorekeeperPerformanceMetrics, ScorekeeperAchievement, LiveGameSession } from '@/services/scorekeeperService';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';

export const useScorekeeperData = () => {
  const { user } = useAuth();
  const { user: internalUser } = useInternalAuth();
  const [performanceMetrics, setPerformanceMetrics] = useState<ScorekeeperPerformanceMetrics | null>(null);
  const [achievements, setAchievements] = useState<ScorekeeperAchievement[]>([]);
  const [activeSessions, setActiveSessions] = useState<LiveGameSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const currentUser = user || internalUser;
  const userId = currentUser?.id;

  const loadScorekeeperData = async () => {
    if (!userId) return;
    
    try {
      setLoading(true);
      setError(null);

      const [metricsResult, achievementsResult, sessionsResult] = await Promise.all([
        scorekeeperService.getPerformanceMetrics(userId),
        scorekeeperService.getAchievements(userId),
        scorekeeperService.getActiveSessions(userId)
      ]);

      setPerformanceMetrics(metricsResult);
      setAchievements(achievementsResult);
      setActiveSessions(sessionsResult);
    } catch (err) {
      console.error('Error loading scorekeeper data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load scorekeeper data');
    } finally {
      setLoading(false);
    }
  };

  const refreshData = () => {
    loadScorekeeperData();
  };

  useEffect(() => {
    loadScorekeeperData();
  }, [userId]);

  return {
    performanceMetrics,
    achievements,
    activeSessions,
    loading,
    error,
    refreshData,
    userId
  };
};